import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  FiCalendar,
  FiChevronLeft,
  FiChevronRight,
  FiCreditCard,
  FiPackage,
  FiMoreVertical,
  FiX,
  FiRefreshCw,
} from "../../../assets/icons/vander";
import BottomNav from "../../../components/bottomnav.jsx";
import Footer from "../../../components/footer";
import Navbar from "../../../components/navbar";
import ScrollToTop from "../../../components/scroll-to-top";
import Switcher from "../../../components/switcher";
import Usertab from "../../../components/user-tab";

export default function UserAccount() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalOrders, setTotalOrders] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const pageSize = 5; // Number of orders per page
  const BASE_MEDIA_URL = process.env.REACT_APP_MEDIA_BASE_URL;

  useEffect(() => {
    fetchOrders();
  }, [currentPage]);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const customerId = localStorage.getItem("customer_id");
      if (!customerId) {
        setError("Customer ID not found in local storage");
        setLoading(false);
        return;
      }

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/get-orders/?customer_id=${customerId}&page=${currentPage}&page_size=${pageSize}`
      );

      if (!response.ok) {
        throw new Error("Failed to fetch orders");
      }

      const data = await response.json();
      setOrders(data.orders);
      setTotalOrders(data.total_orders);
      setTotalPages(data.total_pages);
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const options = { year: "numeric", month: "short", day: "numeric" };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      case "order placed":
        return "bg-blue-100 text-blue-800";
      case "order confirmed":
        return "bg-green-100 text-green-800";
      case "ready for shipment":
        return "bg-purple-100 text-purple-800";
      case "shipped":
        return "bg-indigo-100 text-indigo-800";
      case "in-transit":
        return "bg-teal-100 text-teal-800";
      case "out for delivery":
        return "bg-orange-100 text-orange-800";
      case "delivered":
        return "bg-green-100 text-green-800";
      case "cancelled":
        return "bg-red-100 text-red-800";
      case "refunded proceeded":
        return "bg-gray-100 text-gray-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getLatestStatus = (statusHistory) => {
    if (!statusHistory || statusHistory.length === 0) return "Unknown";
    return statusHistory[statusHistory.length - 1].status;
  };

  const getPaymentMethodDisplay = (paymentMethod) => {
    if (paymentMethod && paymentMethod.toLowerCase() === "cod") {
      return {
        text: "Cash on Delivery",
        colorClass: "text-orange-600 dark:text-orange-400",
      };
    } else {
      return {
        text: "Online",
        colorClass: "text-blue-600 dark:text-blue-400",
      };
    }
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const toggleDropdown = (orderId) => {
    setOpenDropdownId(openDropdownId === orderId ? null : orderId);
  };

  const handleCancelOrder = (orderId) => {
    // Implement cancel order logic
    alert(`Cancelling order ${orderId}`);
    setOpenDropdownId(null);
    // After successful cancellation, you might want to refresh the orders
    fetchOrders();
  };

  const handleReturnExchange = (orderId) => {
    // Implement return/exchange logic
    alert(`Processing return/exchange for order ${orderId}`);
    setOpenDropdownId(null);
  };

  const isCancelAllowed = (status) => {
    const statusLower = status.toLowerCase();
    return !['shipped', 'in-transit', 'out for delivery', 'delivered', 'cancelled', 'refunded proceeded'].includes(statusLower);
  };

  const isReturnExchangeAllowed = (status, deliveryDate) => {
    const statusLower = status.toLowerCase();
    if (statusLower !== 'delivered') return false;
    
    // Check if delivery was within the last 24 hours
    const delivery = new Date(deliveryDate);
    const now = new Date();
    const hoursSinceDelivery = (now - delivery) / (1000 * 60 * 60);
    
    return hoursSinceDelivery <= 24;
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    return (
      <div className="flex justify-center items-center mt-8 space-x-2">
        <button
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className={`p-2 rounded-full ${
            currentPage === 1
              ? "bg-gray-200 text-gray-400"
              : "bg-gray-200 text-gray-700 hover:bg-gray-300"
          }`}
        >
          <FiChevronLeft />
        </button>

        {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
          <button
            key={page}
            onClick={() => handlePageChange(page)}
            className={`w-10 h-10 rounded-full ${
              currentPage === page
                ? "bg-green-500 text-white"
                : "bg-gray-200 text-gray-700 hover:bg-gray-300"
            }`}
          >
            {page}
          </button>
        ))}

        <button
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className={`p-2 rounded-full ${
            currentPage === totalPages
              ? "bg-gray-200 text-gray-400"
              : "bg-gray-200 text-gray-700 hover:bg-gray-300"
          }`}
        >
          <FiChevronRight />
        </button>
      </div>
    );
  };

  return (
    <>
      <Navbar navClass="defaultscroll is-sticky" />

      <section className="relative lg:pb-24 pb-16 md:mt-[84px] mt-[70px]">
        <div className="md:container container-fluid relative">
          <div className="relative overflow-hidden md:rounded-md shadow dark:shadow-gray-700 h-52 bg-[url('../../assets/images/hero/pages.jpg')] bg-center bg-no-repeat bg-cover"></div>
        </div>

        <div className="container relative md:mt-24 mt-16">
          <div className="md:flex">
            <Usertab />

            <div className="lg:w-3/4 md:w-2/3 md:px-3 mt-6 md:mt-0">
              <div className="flex justify-between items-center mb-6">
                <h5 className="text-xl font-semibold">My Orders</h5>
                <p className="text-gray-500">Total Orders: {totalOrders}</p>
              </div>

              {loading ? (
                <div className="flex justify-center items-center h-64">
                  <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-500"></div>
                </div>
              ) : error ? (
                <div className="bg-red-50 text-red-700 p-4 rounded-lg mb-6">
                  Error: {error}
                </div>
              ) : orders.length === 0 ? (
                <div className="bg-gray-50 p-8 rounded-lg text-center">
                  <FiPackage className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-4 text-lg font-medium text-gray-900">
                    No orders found
                  </h3>
                  <p className="mt-2 text-gray-500">
                    You haven't placed any orders yet.
                  </p>
                  <Link
                    to="/shop"
                    className="mt-6 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-500 hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                  >
                    Start Shopping
                  </Link>
                </div>
              ) : (
                <>
                  <div className="space-y-6">
                    {orders.map((order) => {
                      const paymentMethod = getPaymentMethodDisplay(
                        order.payment?.payment_method
                      );
                      const productName =
                        order.items.length > 0
                          ? order.items[0].product_name
                          : "Product";
                      const latestStatus = getLatestStatus(order.status_history);
                      const deliveryDate = order.expected_delivery_date;

                      return (
                        <div
                          key={order.order_id}
                          className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 hover:shadow-md transition-shadow duration-300 relative"
                        >
                          {/* Dropdown Menu Button */}
                          <div className="absolute top-4 right-4 z-10">
                            <button
                              onClick={() => toggleDropdown(order.order_id)}
                              className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800"
                            >
                              <FiMoreVertical className="h-5 w-5 text-gray-500" />
                            </button>
                            
                            {openDropdownId === order.order_id && (
                              <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-slate-800 rounded-md shadow-lg py-1 z-20 border border-gray-200 dark:border-gray-700">
                                {isCancelAllowed(latestStatus) && (
                                  <button
                                    onClick={() => handleCancelOrder(order.order_id)}
                                    className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                                  >
                                    <FiX className="mr-2 h-4 w-4" />
                                    Cancel Order
                                  </button>
                                )}
                                
                                {isReturnExchangeAllowed(latestStatus, deliveryDate) && (
                                  <button
                                    onClick={() => handleReturnExchange(order.order_id)}
                                    className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                                  >
                                    <FiRefreshCw className="mr-2 h-4 w-4" />
                                    Return/Exchange
                                  </button>
                                )}
                              </div>
                            )}
                          </div>

                          {/* Mobile View */}
                          <div className="md:hidden">
                            {/* Top Section - Product Name */}
                            <div className="p-4 border-b border-gray-100 dark:border-gray-800">
                              <p className="font-medium text-gray-900 dark:text-white truncate">
                                {productName}
                              </p>
                            </div>

                            {/* Order ID and Invoice No. Section - Line by Line */}
                            <div className="p-4 border-b border-gray-100 dark:border-gray-800">
                              <div className="mb-2">
                                <div className="flex justify-between">
                                  <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                    Order ID
                                  </p>
                                  <p className="font-medium text-gray-900 dark:text-white truncate max-w-[60%]">
                                    {order.order_id}
                                  </p>
                                </div>
                              </div>
                              <div>
                                <div className="flex justify-between">
                                  <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                    Invoice No.
                                  </p>
                                  <p className="font-medium text-gray-900 dark:text-white truncate max-w-[60%]">
                                    {order.invoice_no || "N/A"}
                                  </p>
                                </div>
                              </div>
                            </div>

                            {/* Status Badge */}
                            <div className="px-4 pt-4">
                              <span
                                className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(
                                  latestStatus
                                )}`}
                              >
                                {latestStatus}
                              </span>
                            </div>

                            {/* Order Details */}
                            <div className="p-4">
                              <div className="grid grid-cols-2 gap-4">
                                <div>
                                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                                    Order Date
                                  </p>
                                  <div className="flex items-center text-sm text-gray-900 dark:text-white">
                                    <FiCalendar className="mr-2 h-4 w-4 text-gray-400" />
                                    {formatDate(order.order_date)}
                                  </div>
                                </div>
                                <div>
                                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                                    Delivery Date
                                  </p>
                                  <div className="flex items-center text-sm text-gray-900 dark:text-white">
                                    <FiCalendar className="mr-2 h-4 w-4 text-gray-400" />
                                    {formatDate(deliveryDate)}
                                  </div>
                                </div>
                                <div>
                                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                                    Payment
                                  </p>
                                  <div className="flex items-center text-sm">
                                    <FiCreditCard className="mr-2 h-4 w-4 text-gray-400" />
                                    <span className={paymentMethod.colorClass}>
                                      {paymentMethod.text}
                                    </span>
                                  </div>
                                </div>
                                <div>
                                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                                    Items
                                  </p>
                                  <div className="flex items-center text-sm text-gray-900 dark:text-white">
                                    <FiPackage className="mr-2 h-4 w-4 text-gray-400" />
                                    {order.items.length} items
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Mobile Footer with Amount */}
                            <div className="px-4 py-3 bg-gray-50 dark:bg-slate-800 border-t border-gray-100 dark:border-gray-800">
                              <div className="flex justify-between items-center">
                                <div>
                                  <p className="text-xs text-gray-500 dark:text-gray-400">
                                    Amount
                                  </p>
                                  <p className="font-semibold text-gray-900 dark:text-white">
                                    ₹{order.total_amount}
                                  </p>
                                </div>
                                <Link
                                  to={`/order-details/${order.order_id}`}
                                  className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md text-white bg-green-500 hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                                >
                                  View Details
                                </Link>
                              </div>
                            </div>
                          </div>

                          {/* Desktop View */}
                          <div className="hidden md:block">
                            <div className="px-6 pb-2 pt-6">
                              {/* Product Name at Top Left */}
                              <div className="mb-4">
                                <p className="font-medium text-gray-900 dark:text-white truncate">
                                  {productName}
                                </p>
                              </div>

                              <div className="flex justify-between items-start mb-4">
                                <div>
                                  <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">
                                    Order ID
                                  </p>
                                  <p className="font-medium text-gray-900 dark:text-white">
                                    {order.order_id}
                                  </p>
                                </div>
                                <div className="text-right">
                                  <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">
                                    Invoice No.
                                  </p>
                                  <p className="font-medium text-gray-900 dark:text-white">
                                    {order.invoice_no || "N/A"}
                                  </p>
                                </div>
                                <div>
                                  <span
                                    className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(
                                      latestStatus
                                    )}`}
                                  >
                                    {latestStatus}
                                  </span>
                                </div>
                              </div>

                              <div className="grid grid-cols-4 gap-4 mb-2">
                                <div>
                                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                                    Order Date
                                  </p>
                                  <div className="flex items-center text-sm text-gray-900 dark:text-white">
                                    <FiCalendar className="mr-2 h-4 w-4 text-gray-400" />
                                    {formatDate(order.order_date)}
                                  </div>
                                </div>
                                <div>
                                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                                    Delivery Date
                                  </p>
                                  <div className="flex items-center text-sm text-gray-900 dark:text-white">
                                    <FiCalendar className="mr-2 h-4 w-4 text-gray-400" />
                                    {formatDate(deliveryDate)}
                                  </div>
                                </div>
                                <div>
                                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                                    Payment
                                  </p>
                                  <div className="flex items-center text-sm">
                                    <FiCreditCard className="mr-2 h-4 w-4 text-gray-400" />
                                    <span className={paymentMethod.colorClass}>
                                      {paymentMethod.text}
                                    </span>
                                  </div>
                                </div>
                                <div>
                                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                                    Items
                                  </p>
                                  <div className="flex items-center text-sm text-gray-900 dark:text-white">
                                    <FiPackage className="mr-2 h-4 w-4 text-gray-400" />
                                    {order.items.length} items
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Desktop Footer with Amount */}
                            <div className="px-6 py-2 bg-gray-50 dark:bg-slate-800 border-t border-gray-100 dark:border-gray-800">
                              <div className="flex justify-between items-center">
                                <div>
                                  <p className="text-xs text-gray-500 dark:text-gray-400">
                                    Amount
                                  </p>
                                  <p className="font-semibold text-gray-900 dark:text-white">
                                    ₹{order.total_amount}
                                  </p>
                                </div>
                                <Link
                                  to={`/order-details/${order.order_id}`}
                                  className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md text-white bg-green-500 hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                                >
                                  View Details
                                </Link>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {renderPagination()}
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      <BottomNav />
      <Footer />
      <Switcher />
      <ScrollToTop />
    </>
  );
}